import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { store } from '@/store';
import { logout } from '@/store/authSlice';
import { DemoModeError, resolveDemoRequest } from './demoMode';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://relay.sspwallet.com';

export const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/v1/enterprise`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — demo firewall FIRST, then auth token.
//
// DEMO SAFETY (DEMO_TREASURY_DESIGN.md §3.3 / §8): while `auth.isDemo` is true
// we short-circuit BEFORE attaching any Authorization header and BEFORE the
// request can leave the browser. A safe GET may be served from a bundled
// fixture (via config.adapter, so nothing hits the network); everything else is
// hard-rejected with DemoModeError. Demo never mints a sessionToken, so even if
// this guard were bypassed there would be no credential to attach.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const state = store.getState();

    if (state.auth.isDemo) {
      try {
        const demoResponse = resolveDemoRequest(config);
        // Replace the network adapter with one that returns the fixture
        // response synchronously. The request never touches the relay and no
        // auth header is attached below.
        config.adapter = () => Promise.resolve(demoResponse);
        return config;
      } catch (demoErr) {
        // No fixture for this request — reject so it can never be sent.
        return Promise.reject(
          demoErr instanceof DemoModeError ? demoErr : new DemoModeError(config.url)
        );
      }
    }

    const session = state.auth.session;
    if (session?.sessionToken) {
      config.headers.Authorization = `Bearer ${session.sessionToken}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// 429 retry with backoff. The relay rate-limits per client IP, so a portfolio
// fan-out or an office NAT sharing one bucket can throttle individual requests
// transiently. express-rate-limit rejects BEFORE the handler runs, so a 429
// response means the request was never processed — retrying any method is safe.
// Delay comes from the server's own headers when present (Retry-After, or the
// draft-8 `RateLimit: ...; t=<seconds>` reset hint), else exponential backoff.
const MAX_RATE_LIMIT_RETRIES = 2;
const MAX_RATE_LIMIT_DELAY_MS = 15000;

function rateLimitDelayMs(headers: Record<string, unknown>, attempt: number): number {
  const retryAfter = headers['retry-after'];
  if (typeof retryAfter === 'string') {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) {
      return Math.min(seconds * 1000, MAX_RATE_LIMIT_DELAY_MS);
    }
  }
  const rateLimit = headers['ratelimit'];
  if (typeof rateLimit === 'string') {
    const match = /(?:^|;)\s*t=(\d+)/.exec(rateLimit);
    if (match) {
      return Math.min(Number(match[1]) * 1000, MAX_RATE_LIMIT_DELAY_MS);
    }
  }
  // No server hint — 1s, 2s (+ jitter to de-synchronize parallel retries).
  return Math.min(1000 * 2 ** (attempt - 1) + Math.random() * 500, MAX_RATE_LIMIT_DELAY_MS);
}

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const requestConfig = error.config as
      | (InternalAxiosRequestConfig & { rateLimitRetries?: number })
      | undefined;
    if (
      error.response?.status === 429 &&
      requestConfig &&
      (requestConfig.rateLimitRetries ?? 0) < MAX_RATE_LIMIT_RETRIES
    ) {
      requestConfig.rateLimitRetries = (requestConfig.rateLimitRetries ?? 0) + 1;
      const delay = rateLimitDelayMs(
        error.response.headers as Record<string, unknown>,
        requestConfig.rateLimitRetries
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
      return apiClient(requestConfig);
    }

    if (error.response?.status === 401) {
      store.dispatch(logout());
    }

    // Extract server error message from SSP API response format
    // { status: 'error', data: { message: '...' } }
    const responseData = error.response?.data as
      | { status?: string; data?: { message?: string } }
      | undefined;
    if (responseData?.data?.message) {
      // Preserve the HTTP status so callers can discriminate permission errors
      // (4xx) from transient network/server errors (5xx) without losing the
      // friendly message.
      const wrapped = new Error(responseData.data.message) as Error & {
        status?: number;
      };
      if (typeof error.response?.status === 'number') wrapped.status = error.response.status;
      return Promise.reject(wrapped);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
